import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import USMap from "../components/USMap";
import { NETWORK } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    request_type: "Prayer Request",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/prayer-requests`, form);
    } catch {
      /* best-effort logging to dashboard */
    }
    const subject = encodeURIComponent(`[${form.request_type}] from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nType: ${form.request_type}\n\n${form.message}`
    );
    window.location.href = `mailto:${NETWORK.email}?subject=${subject}&body=${body}`;
    toast.success("Opening your email to send your request to our team.");
    setForm({ name: "", email: "", request_type: "Prayer Request", message: "" });
    setLoading(false);
  };

  const inputCls =
    "bg-black border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-primary h-12 px-0 text-foreground font-body";

  return (
    <div data-testid="contact-page">
      <PageHeader overline="Social · Online · Mobile" lines={["Connect"]} />

      {/* About Us */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto pb-16 md:pb-24" data-testid="about-us">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-border pt-12">
          <Reveal className="md:col-span-4">
            <p className="overline text-primary">About Us</p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-8">
            <p className="font-editorial text-3xl md:text-5xl text-foreground leading-tight">
              {NETWORK.about}
            </p>
          </Reveal>
        </div>
      </section>

      <section id="connect-form" className="px-6 md:px-10 max-w-[1600px] mx-auto pb-32 grid grid-cols-1 md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-5">
          <h2 className="font-editorial text-4xl md:text-6xl text-foreground leading-tight">
            Submit your prayer request or connect with the Network.
          </h2>
          <p className="font-body text-muted-foreground mt-8 leading-relaxed max-w-md">
            {NETWORK.mission}
          </p>
          <div className="mt-8">
            <p className="overline text-muted-foreground mb-2">Email Us Directly</p>
            <a
              href={`mailto:${NETWORK.email}`}
              data-testid="contact-email-link"
              className="font-editorial text-2xl text-secondary hover:text-foreground transition-colors duration-300 border-b border-secondary/50 pb-1"
            >
              {NETWORK.email}
            </a>
          </div>
          <p className="font-editorial italic text-secondary text-xl mt-10">{NETWORK.seal}</p>
        </Reveal>

        <Reveal delay={0.15} className="md:col-span-7">
          <form onSubmit={submit} className="space-y-6" data-testid="prayer-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input data-testid="contact-name" placeholder="Full Name" value={form.name} onChange={set("name")} className={inputCls} />
              <Input data-testid="contact-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-3">Request Type</label>
              <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                <SelectTrigger data-testid="contact-type" className="bg-black border-0 border-b border-white/20 rounded-none h-12 px-0 focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border rounded-none">
                  {["Prayer Request", "Salvation", "Partnership", "ALU Enrollment", "General Inquiry"].map((r) => (
                    <SelectItem key={r} value={r} className="rounded-none">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              data-testid="contact-message"
              placeholder="Your message or prayer request..."
              rows={5}
              value={form.message}
              onChange={set("message")}
              className="bg-black border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-primary px-0 text-foreground font-body resize-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="group bg-primary text-primary-foreground overline px-10 py-5 flex items-center gap-3 hover:bg-primary/85 transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Request"}
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </button>
          </form>
        </Reveal>
      </section>

      {/* Network Reach — US Map */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto pb-32" data-testid="network-reach">
        <Reveal className="mb-10">
          <p className="overline text-primary mb-4">Our Reach</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground">Network Churches</h2>
          <p className="font-editorial italic text-xl md:text-2xl text-muted-foreground mt-6 max-w-2xl">
            From coast to coast, EKG Network churches are dwelling in the high places across nine states — and rising.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <USMap />
        </Reveal>
      </section>

      {/* Join EKG CTA */}
      <section className="px-6 md:px-10 pb-32" data-testid="join-ekg">
        <div className="max-w-[1600px] mx-auto border border-border bg-muted/20 px-8 py-16 md:px-16 md:py-24 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <Reveal className="relative">
            <p className="overline text-secondary mb-5">Get More Information</p>
            <h2 className="font-display uppercase text-5xl md:text-8xl text-foreground leading-[0.9]">
              Join EKG
            </h2>
            <p className="font-editorial italic text-2xl md:text-3xl text-muted-foreground mt-8 max-w-2xl">
              Become part of an apostolic reformation. Whether you are a saint, a minister, or a ministry seeking covenant covering — we would love to hear from you.
            </p>
            <a
              href={`mailto:${NETWORK.email}?subject=${encodeURIComponent("Join EKG — Request for Information")}`}
              data-testid="join-ekg-btn"
              className="group inline-flex items-center gap-3 mt-10 bg-primary text-primary-foreground overline px-8 py-5 hover:bg-primary/85 transition-colors duration-300"
            >
              Get More Information
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
